import React, { useState } from "react";
import { useQuery } from "react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line } from "recharts";
import { BsFacebook, BsGlobe2, BsGraphUpArrow, BsTrophy, BsExclamationTriangle } from "react-icons/bs";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import useAnalyticsDataEnrichment from "../../../../hooks/useAnalyticsDataEnrichment";
import { formatNumber, formatPercentage, truncate } from "../../../../utils/analyticsDashboardFormat";
import {
  WidgetSection,
  MetricsGrid,
  MetricCard,
  MetricCardHeader,
  MetricLabel,
  MetricIconWrap,
  MetricValue,
  ChartCard,
  ChartCardTitle,
  SkeletonBlock,
  WidgetTable,
  TableScroll,
  SubTabsBar,
  SubTabButton,
  StatusPill,
  EmptyStateBox,
  MetricTooltip,
} from "./Widgets.styles";

const FacebookFormPerformanceWidget = ({ query }) => {
  const [subTab, setSubTab] = useState("forms");
  const dataEnrichment = useAnalyticsDataEnrichment(query.companyId);

  const { data, isLoading, error } = useQuery(
    ["facebook-form-performance", query.companyId, query.startDate, query.endDate, query.channelIds, query.granularity, query.formId],
    () => analyticsDashboardApi.getFacebookFormPerformance(query),
    { enabled: !!query.companyId }
  );

  if (isLoading) {
    return (
      <MetricsGrid columns={4}>
        {[...Array(4)].map((_, i) => (
          <MetricCard key={i}><SkeletonBlock style={{ height: 48 }} /></MetricCard>
        ))}
      </MetricsGrid>
    );
  }

  if (error) {
    return (
      <EmptyStateBox>
        <BsExclamationTriangle size={32} style={{ color: "#DC2626", marginBottom: 12 }} />
        <div>Error al cargar rendimiento de formularios de Facebook</div>
      </EmptyStateBox>
    );
  }

  if (!data?.data) {
    return (
      <EmptyStateBox>
        <BsFacebook size={32} style={{ color: "#9CA3AF", marginBottom: 12 }} />
        <div>No hay datos de formularios de Facebook disponibles para el período seleccionado.</div>
      </EmptyStateBox>
    );
  }

  const {
    summary = {},
    formPerformance = [],
    organicPerformance = { totalClients: 0, finishedClients: 0, conversionRate: 0 },
    comparison = {},
  } = data.data;

  const getFormDisplayName = (formId) => {
    if (dataEnrichment.isReady) {
      const formName = dataEnrichment.getFormName(formId);
      return formName !== formId ? formName : `Formulario ${formId}`;
    }
    return `Formulario ${formId}`;
  };

  const enrichedFormPerformance = formPerformance.map((form) => ({
    ...form,
    formDisplayName: getFormDisplayName(form.formId),
    formDisplayNameShort: truncate(getFormDisplayName(form.formId), 15),
  }));

  const tooltipStyle = {
    contentStyle: {
      background: "white",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      boxShadow: "0 6px 18px rgba(15,23,42,0.10)",
      fontFamily: "'Inter', sans-serif",
      fontSize: "0.82rem",
    },
    labelStyle: { fontWeight: 700, color: "#111827", marginBottom: 4 },
    itemStyle: { color: "#4B5563" },
  };

  return (
    <WidgetSection>
      <MetricsGrid columns={4}>
        <MetricCard accentColor="#3b82f6">
          <MetricCardHeader>
            <MetricLabel>
              Total de Formularios
              <MetricTooltip text="Número de formularios de Facebook Lead Ads distintos que generaron al menos una conversación en el período." />
            </MetricLabel>
            <MetricIconWrap bg="#EFF6FF" color="#3b82f6"><BsFacebook /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(summary.totalForms)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricCardHeader>
            <MetricLabel>
              Tasa Formularios FB
              <MetricTooltip text="Tasa de conversión promedio ponderada de todas las conversaciones originadas por formularios de Facebook: (Finalizadas / Total) × 100." />
            </MetricLabel>
            <MetricIconWrap bg="#F0FDF4" color="#10b981"><BsGraphUpArrow /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatPercentage(summary.overallFbFormConversionRate)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#8b5cf6">
          <MetricCardHeader>
            <MetricLabel>
              Tasa Orgánica
              <MetricTooltip text="Tasa de conversión de conversaciones que llegaron de forma orgánica (sin formulario ni anuncio de Facebook)." />
            </MetricLabel>
            <MetricIconWrap bg="#F5F3FF" color="#8b5cf6"><BsGlobe2 /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatPercentage(summary.organicConversionRate)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#f59e0b">
          <MetricCardHeader>
            <MetricLabel>
              Mejor Tasa de Formulario
              <MetricTooltip text="La tasa de conversión más alta entre todos los formularios activos en el período. Sirve como referencia de rendimiento óptimo." />
            </MetricLabel>
            <MetricIconWrap bg="#FFFBEB" color="#f59e0b"><BsTrophy /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatPercentage(comparison.bestPerformingForm?.conversionRate || 0)}</MetricValue>
        </MetricCard>
      </MetricsGrid>

      {comparison.bestPerformingForm && comparison.worstPerformingForm && (
        <MetricsGrid columns={3}>
          <MetricCard accentColor="#10b981" style={{ background: "#F0FDF4", borderColor: "#BBF7D0" }}>
            <MetricLabel>Formulario de Mejor Rendimiento</MetricLabel>
            <div style={{ fontWeight: 700, color: "#14532D", marginTop: 8 }}>{getFormDisplayName(comparison.bestPerformingForm.formId)}</div>
            <p style={{ fontSize: "0.8rem", color: "#166534", margin: "4px 0 0" }}>{formatPercentage(comparison.bestPerformingForm.conversionRate)} tasa de conversión</p>
          </MetricCard>
          <MetricCard accentColor="#ef4444" style={{ background: "#FEF2F2", borderColor: "#FECACA" }}>
            <MetricLabel>Formulario con Menor Rendimiento</MetricLabel>
            <div style={{ fontWeight: 700, color: "#7F1D1D", marginTop: 8 }}>{getFormDisplayName(comparison.worstPerformingForm.formId)}</div>
            <p style={{ fontSize: "0.8rem", color: "#991B1B", margin: "4px 0 0" }}>{formatPercentage(comparison.worstPerformingForm.conversionRate)} tasa de conversión</p>
          </MetricCard>
          <MetricCard accentColor="#3b82f6" style={{ background: "#EFF6FF", borderColor: "#BFDBFE" }}>
            <MetricLabel>Orgánico vs Mejor Formulario</MetricLabel>
            <MetricValue style={{ color: (comparison.organicVsBestForm?.difference || 0) > 0 ? "#16A34A" : "#DC2626" }}>{(comparison.organicVsBestForm?.difference || 0) > 0 ? "+" : ""}{(comparison.organicVsBestForm?.difference || 0).toFixed(1)}%</MetricValue>
            <p style={{ fontSize: "0.8rem", color: "#1D4ED8", margin: "4px 0 0" }}>{(comparison.organicVsBestForm?.difference || 0) > 0 ? "Orgánico rinde mejor" : "Formularios rinden mejor"}</p>
          </MetricCard>
        </MetricsGrid>
      )}

      <SubTabsBar>
        <SubTabButton active={subTab === "forms"} onClick={() => setSubTab("forms")}>Rendimiento de Formularios</SubTabButton>
        <SubTabButton active={subTab === "comparison"} onClick={() => setSubTab("comparison")}>Orgánico vs Formularios</SubTabButton>
        <SubTabButton active={subTab === "data"} onClick={() => setSubTab("data")}>Tabla de Datos</SubTabButton>
      </SubTabsBar>

      {subTab === "forms" && (
        <ChartCard>
          <ChartCardTitle>
            Rendimiento Individual de Formularios
            <MetricTooltip text="Comparación por formulario: total de conversaciones captadas vs conversaciones finalizadas, con la tasa de conversión superpuesta en el eje derecho." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={480}>
            <BarChart data={enrichedFormPerformance} margin={{ top: 8, right: 24, left: 0, bottom: 120 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="formDisplayNameShort" angle={-45} textAnchor="end" height={120} interval={0} tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <Tooltip
                labelFormatter={(label) => {
                  const item = enrichedFormPerformance.find((f) => f.formDisplayNameShort === label);
                  return item ? `${item.formDisplayName} (ID: ${item.formId})` : label;
                }}
                {...tooltipStyle}
              />
              <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
              <Bar yAxisId="left" dataKey="totalClients" fill="#3b82f6" name="Total de Conversaciones" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="finishedClients" fill="#10b981" name="Conv. Finalizadas" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="conversionRate" stroke="#8b5cf6" strokeWidth={2.5} name="Tasa de Conversión %" dot={false} activeDot={{ r: 5, strokeWidth: 0 }} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {subTab === "comparison" && (
        <ChartCard>
          <ChartCardTitle>
            Comparación Orgánico vs Formularios de Facebook
            <MetricTooltip text="Contraste entre la tasa de conversión y el volumen de conversaciones generadas por tráfico orgánico frente a formularios de Facebook Lead Ads." />
          </ChartCardTitle>
          <MetricsGrid columns={2}>
            <div>
              <h4 style={{ marginBottom: 8 }}>Métricas de Rendimiento</h4>
              <p>Tasa Formularios FB: <strong style={{ color: "#2563EB" }}>{formatPercentage(summary.overallFbFormConversionRate)}</strong></p>
              <p>Tasa Orgánica: <strong style={{ color: "#16A34A" }}>{formatPercentage(summary.organicConversionRate)}</strong></p>
              <p>Diferencia: <strong style={{ color: (comparison.organicVsBestForm?.difference || 0) > 0 ? "#16A34A" : "#DC2626" }}>{(comparison.organicVsBestForm?.difference || 0) > 0 ? "+" : ""}{(comparison.organicVsBestForm?.difference || 0).toFixed(1)}%</strong></p>
            </div>
            <div>
              <h4 style={{ marginBottom: 8 }}>Métricas de Volumen</h4>
              <p>Conv. Formularios FB: <strong>{formatNumber(summary.totalFbFormClients)}</strong></p>
              <p>Conv. Orgánicas: <strong>{formatNumber(summary.organicClients)}</strong></p>
              <p>Total: <strong>{formatNumber(summary.totalFbFormClients + summary.organicClients)}</strong></p>
            </div>
          </MetricsGrid>
        </ChartCard>
      )}

      {subTab === "data" && (
        <ChartCard>
          <ChartCardTitle>
            Datos de Rendimiento de Formularios
            <MetricTooltip text="Tabla detallada por formulario con total de conversaciones captadas, finalizadas y tasa de conversión individual." />
          </ChartCardTitle>
          <TableScroll>
            <WidgetTable>
              <thead>
                <tr>
                  <th>Nombre del Formulario</th>
                  <th className="align-right">Total de Conv.</th>
                  <th className="align-right">Conv. Finalizadas</th>
                  <th className="align-right">Tasa de Conversión</th>
                </tr>
              </thead>
              <tbody>
                {enrichedFormPerformance.map((form) => (
                  <tr key={form.formId}>
                    <td>
                      <div>{form.formDisplayName}</div>
                      <div style={{ fontSize: "0.7rem", color: "#9CA3AF" }}>{form.formId}</div>
                    </td>
                    <td className="align-right">{formatNumber(form.totalClients)}</td>
                    <td className="align-right">{formatNumber(form.finishedClients)}</td>
                    <td className="align-right">
                      <StatusPill variant={form.conversionRate >= 50 ? "good" : form.conversionRate >= 25 ? "warn" : "bad"}>
                        {formatPercentage(form.conversionRate)}
                      </StatusPill>
                    </td>
                  </tr>
                ))}
                <tr style={{ background: "#F0FDF4" }}>
                  <td><strong>Tráfico Orgánico</strong></td>
                  <td className="align-right"><strong>{formatNumber(organicPerformance.totalClients)}</strong></td>
                  <td className="align-right"><strong>{formatNumber(organicPerformance.finishedClients)}</strong></td>
                  <td className="align-right">
                    <StatusPill variant={organicPerformance.conversionRate >= 50 ? "good" : organicPerformance.conversionRate >= 25 ? "warn" : "bad"}>
                      {formatPercentage(organicPerformance.conversionRate)}
                    </StatusPill>
                  </td>
                </tr>
              </tbody>
            </WidgetTable>
          </TableScroll>
        </ChartCard>
      )}
    </WidgetSection>
  );
};

export default FacebookFormPerformanceWidget;
